/**
 * LifecyclePreview — pestaña "Vista del usuario" del Lifecycle Designer
 * (B.6.d.9.f + B.6.d.9.i — modelo "deliverable" 1:1).
 *
 * Renderiza el lifecycle como lo verá el equipo del proyecto al crearlo:
 * cada phase es un card con su lista de **entregables** (sub-fases con
 * artifact ligado 1:1).
 *
 * Por entregable mostramos:
 *   <subphase_code> · <subphase_name>   → <template_key>
 *                                        ×N · opc · ∞
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { FileText, Infinity as InfinityIcon, Upload, UserRound } from 'lucide-react'

import {
  listTemplates,
  type ExpectedArtifact,
  type LifecycleContractMode,
  type LifecyclePhase,
  type LifecyclePhaseI18n,
} from '../../../api/templates'
import { useAccessToken } from '../../../auth/useAccessToken'
import {
  findPhaseI18n,
  type LifecycleDesignerState,
} from './lifecycleState'
import type { SupportedLocale } from '../../../i18n/config'

interface LifecyclePreviewProps {
  state: LifecycleDesignerState
}

const CONTRACT_MODE_BADGE: Record<
  LifecycleContractMode,
  { i18nKey: string; className: string }
> = {
  internal: {
    i18nKey: 'designer.lifecycle.contract_mode_internal',
    className: 'bg-brand-soft text-brand',
  },
  skipped: {
    i18nKey: 'designer.lifecycle.contract_mode_skipped',
    className: 'bg-surface-muted text-ink-subtle',
  },
}

export function LifecyclePreview({ state }: LifecyclePreviewProps) {
  const { t } = useTranslation('templates')
  const { accessToken } = useAccessToken()
  const { definition, activeLocale } = state
  const block = definition.i18n[activeLocale]
  const phases = [...definition.phases].sort(
    (a, b) => a.phase_order - b.phase_order,
  )

  // Catálogo de roles para resolver template_key → name.
  const { data: roles = [] } = useQuery({
    queryKey: ['templates', 'list', 'role'],
    queryFn: () =>
      listTemplates(accessToken!, { template_type: 'role' }),
    enabled: Boolean(accessToken),
    staleTime: 60_000,
  })
  const roleNameByKey = useMemo(() => {
    const map = new Map<string, string>()
    for (const r of roles) map.set(r.template_key, r.name)
    return map
  }, [roles])

  const totalDeliverables = phases.reduce(
    (acc, p) => acc + (p.subphases?.length ?? 0),
    0,
  )

  return (
    <article className="font-body text-ink space-y-6">
      <header className="border-b border-line pb-4">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
          {t('designer.lifecycle.preview_eyebrow')}
        </p>
        <h1 className="mt-1 font-display text-2xl font-semibold text-ink">
          {block?.name || t('designer.lifecycle.preview_untitled')}
        </h1>
        {block?.description ? (
          <p className="mt-2 font-body text-sm text-ink-muted leading-relaxed max-w-3xl">
            {block.description}
          </p>
        ) : null}
      </header>

      <section className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat
          label={t('designer.lifecycle.stats_phases')}
          value={phases.length}
        />
        <Stat
          label={t('designer.lifecycle.stats_deliverables')}
          value={totalDeliverables}
        />
        <Stat
          label={t('designer.lifecycle.preview_locales_label')}
          value={(Object.keys(definition.i18n) as Array<SupportedLocale>).length}
        />
      </section>

      {phases.length === 0 ? (
        <p className="rounded-md border border-dashed border-line p-8 text-center text-sm text-ink-subtle italic">
          {t('designer.lifecycle.preview_empty')}
        </p>
      ) : (
        <ol className="space-y-5">
          {phases.map((phase, idx) => {
            const phaseI18n = findPhaseI18n(
              definition,
              phase.phase_code,
              activeLocale,
            )
            const badge =
              CONTRACT_MODE_BADGE[phase.default_contract_mode] ??
              CONTRACT_MODE_BADGE.internal
            return (
              <li
                key={phase.phase_code}
                className="rounded-md border border-line bg-surface p-5"
              >
                <PhaseHeader
                  number={idx}
                  phaseCode={phase.phase_code}
                  title={phaseI18n?.name || phase.phase_code}
                  description={phaseI18n?.description}
                  gateDescription={
                    phaseI18n?.gate_description ?? phase.gate_description
                  }
                  contractBadge={badge}
                  overridable={phase.contract_mode_overridable === true}
                />

                <div className="mt-4">
                  <DeliverablesBlock
                    phase={phase}
                    phaseI18n={phaseI18n}
                    roleNameByKey={roleNameByKey}
                  />
                </div>
              </li>
            )
          })}
        </ol>
      )}
    </article>
  )
}

// ─── Sub-components ────────────────────────────────────────────────────────

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-line bg-surface p-3">
      <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
        {label}
      </p>
      <p className="mt-1 font-display text-2xl font-semibold text-ink tabular-nums">
        {value}
      </p>
    </div>
  )
}

function PhaseHeader({
  number,
  phaseCode,
  title,
  description,
  gateDescription,
  contractBadge,
  overridable,
}: {
  number: number
  phaseCode: string
  title: string
  description: string | undefined
  gateDescription: string | undefined
  contractBadge: { i18nKey: string; className: string }
  overridable: boolean
}) {
  const { t } = useTranslation('templates')
  return (
    <header>
      <div className="flex items-baseline gap-2 flex-wrap text-[10px] uppercase tracking-wider text-ink-subtle">
        <span className="font-mono">
          {t('designer.lifecycle.preview_phase_index', { n: number })}
        </span>
        <span>·</span>
        <span className="font-mono">{phaseCode}</span>
        <span
          className={`font-mono px-1.5 rounded ${contractBadge.className}`}
        >
          {t(contractBadge.i18nKey as 'designer.lifecycle.contract_mode_internal')}
        </span>
        {overridable ? (
          <span
            className="font-mono px-1.5 rounded bg-official-soft text-official"
            title={t('designer.lifecycle.contract_overridable_hint')}
          >
            {t('designer.lifecycle.preview_overridable_badge')}
          </span>
        ) : null}
      </div>
      <h2 className="mt-1 font-display text-lg font-semibold text-ink">
        {title}
      </h2>
      {description ? (
        <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">
          {description}
        </p>
      ) : null}
      {gateDescription ? (
        <div className="mt-3 rounded-md border-l-2 border-review bg-review-soft/20 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-review">
            {t('designer.lifecycle.preview_gate_label')}
          </p>
          <p className="mt-0.5 text-sm text-ink leading-relaxed">
            {gateDescription}
          </p>
        </div>
      ) : null}
    </header>
  )
}

function DeliverablesBlock({
  phase,
  phaseI18n,
  roleNameByKey,
}: {
  phase: LifecyclePhase
  phaseI18n: LifecyclePhaseI18n | undefined
  roleNameByKey: Map<string, string>
}) {
  const { t } = useTranslation('templates')
  const subphases = [...(phase.subphases ?? [])].sort(
    (a, b) => a.subphase_order - b.subphase_order,
  )
  const artifactsBySubphase = new Map<string, ExpectedArtifact>(
    (phase.expected_artifacts ?? []).map((a) => [a.subphase_code, a]),
  )

  return (
    <section>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle mb-2">
        {t('designer.lifecycle.preview_deliverables_title', {
          count: subphases.length,
        })}
      </p>
      {subphases.length === 0 ? (
        <p className="text-xs text-ink-subtle italic">
          {t('designer.lifecycle.preview_deliverables_empty')}
        </p>
      ) : (
        <ul className="space-y-2">
          {subphases.map((sp) => {
            const i18n = phaseI18n?.subphases?.find(
              (s) => s.subphase_code === sp.subphase_code,
            )
            const art = artifactsBySubphase.get(sp.subphase_code)
            return (
              <li
                key={sp.subphase_code}
                className="rounded-md border border-line bg-canvas px-3 py-2"
              >
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-mono text-[11px] text-ink-subtle tabular-nums shrink-0">
                    {sp.subphase_code}
                  </span>
                  <span className="font-display text-sm font-semibold text-ink">
                    {i18n?.name ?? sp.subphase_code}
                  </span>
                  {art?.user_can_add_more ? (
                    <span
                      className="inline-flex items-center gap-0.5 font-mono text-[10px] uppercase tracking-wider px-1.5 rounded bg-official-soft text-official"
                      title={t(
                        'designer.lifecycle.artifact_user_can_add_more_label',
                      )}
                    >
                      <InfinityIcon className="w-3 h-3" />
                      {t('designer.lifecycle.preview_user_add_more_badge')}
                    </span>
                  ) : null}
                  {art?.is_optional ? (
                    <span className="font-mono text-[10px] uppercase tracking-wider px-1 rounded bg-surface-muted text-ink-subtle">
                      {t('designer.lifecycle.preview_optional_badge')}
                    </span>
                  ) : (
                    <span className="font-mono text-[10px] uppercase tracking-wider px-1 rounded bg-official-soft/60 text-official">
                      {t('designer.lifecycle.preview_required_badge')}
                    </span>
                  )}
                </div>
                {i18n?.description ? (
                  <p className="mt-1 text-xs text-ink-muted leading-relaxed">
                    {i18n.description}
                  </p>
                ) : null}
                {art ? (
                  <div className="mt-1.5 flex items-center gap-1.5 text-xs text-ink-muted flex-wrap">
                    {art.template_key ? (
                      <>
                        <FileText className="w-3.5 h-3.5 shrink-0" />
                        <span className="font-mono text-[11px]">
                          {art.template_key}
                        </span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-3.5 h-3.5 shrink-0" />
                        <span className="italic text-[11px]">
                          {t(
                            'designer.lifecycle.deliverable_external_kind_label',
                          )}
                        </span>
                      </>
                    )}
                    {(art.default_count ?? 1) > 1 ? (
                      <span className="font-mono text-[10px] text-ink-subtle">
                        ×{art.default_count}
                      </span>
                    ) : null}
                    {art.role_template_key ? (
                      <span
                        className="inline-flex items-center gap-0.5 font-mono text-[10px] uppercase tracking-wider px-1.5 rounded bg-official-soft/60 text-official"
                        title={t(
                          'designer.lifecycle.deliverable_role_label',
                        )}
                      >
                        <UserRound className="w-3 h-3" />
                        {roleNameByKey.get(art.role_template_key) ??
                          art.role_template_key}
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <p className="mt-1 text-[11px] text-critical italic">
                    {t('designer.lifecycle.deliverable_no_artifact')}
                  </p>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
